import React, {
  useState,
  useEffect,
  createContext,
  useContext,
  useCallback,
  useMemo,
  useRef,
} from "react";
import {
  getUser,
  signIn as sendSignInRequest,
  signOut as sendSignOutRequest,
} from "../api/auth";
import { clearLocationsCache } from "../hooks/useLocationFetch";
import { Context } from "../utils/config";
import {
  ALL_LOCATIONS,
  applyUserLocation,
  persistSelectedLocation,
} from "../utils/locationHelpers";
import {
  clearSelectedOrg,
  isSuperAdmin,
  needsOrgSelection,
  persistSelectedOrg,
  readSelectedOrg,
  toEffectiveUser,
} from "../utils/orgSelection";
import {
  MIN_REFRESH_INTERVAL_MS,
  nextRefreshDelay,
  onAuthRejected,
  sessionDecision,
  transientRetryDelay,
  withRefreshedTokens,
} from "../utils/apiFailure";

function AuthProvider(props) {
  const { setLocation } = useContext(Context) || {};
  const [user, setUser] = useState(JSON.parse(sessionStorage.getItem("quser")));
  const [loading, setLoading] = useState(true);
  const [selectedOrg, setSelectedOrg] = useState(() => readSelectedOrg());

  const refreshTimeoutRef = useRef(null);
  const lastRefreshAtRef = useRef(0);
  const retryAttemptRef = useRef(0);
  const signOutRef = useRef(() => {});
  const refreshSessionRef = useRef(async () => {});
  const setupSessionTimersRef = useRef(() => {});

  // Only a super admin may hold an organization selection. One left in this
  // tab by another user would be sent as x-org-id and fail every request.
  const dropSelectionOfOtherUser = useCallback((authUser) => {
    if (isSuperAdmin(authUser) || !readSelectedOrg()) return;
    clearSelectedOrg();
    setSelectedOrg(null);
  }, []);

  const clearSessionTimers = useCallback(() => {
    if (refreshTimeoutRef.current) {
      clearTimeout(refreshTimeoutRef.current);
      refreshTimeoutRef.current = null;
    }
  }, []);

  const signOut = useCallback(() => {
    retryAttemptRef.current = 0;
    setUser();
    sessionStorage.removeItem("quser");
    sessionStorage.removeItem("selectedLocationId");
    sessionStorage.removeItem("selectedLocationName");
    clearSelectedOrg();
    setSelectedOrg(null);
    clearLocationsCache();
    if (typeof setLocation === "function") {
      setLocation(ALL_LOCATIONS);
    }
    try {
      sendSignOutRequest();
    } catch (error) {
      console.warn("Sign out failed", error);
    }
    clearSessionTimers();
  }, [clearSessionTimers, setLocation]);

  signOutRef.current = signOut;

  const readStoredUser = useCallback(() => {
    try {
      const raw = sessionStorage.getItem("quser");
      return raw ? JSON.parse(raw) : null;
    } catch {
      return null;
    }
  }, []);

  const storeUser = useCallback((authUser) => {
    setUser(authUser);
    sessionStorage.setItem("quser", JSON.stringify(authUser));
  }, []);

  // The access token is short. The Cognito refresh token is what keeps the
  // login (30 days in this pool). A failed profile or network call must not
  // delete it. The next attempt is scheduled here; logout waits until Cognito
  // rejects the refresh token.
  const scheduleTransientRetry = useCallback(() => {
    clearSessionTimers();
    const delay = transientRetryDelay(retryAttemptRef.current);
    retryAttemptRef.current += 1;
    refreshTimeoutRef.current = setTimeout(() => {
      void refreshSessionRef.current();
    }, delay);
  }, [clearSessionTimers]);

  const setupSessionTimers = useCallback(
    (authUser) => {
      clearSessionTimers();

      if (!authUser) {
        return;
      }

      const expSeconds =
        authUser?.accessToken?.payload?.exp ?? authUser?.idToken?.payload?.exp;

      if (!expSeconds) {
        return;
      }

      const delay = nextRefreshDelay(expSeconds * 1000);
      refreshTimeoutRef.current = setTimeout(() => {
        void refreshSessionRef.current();
      }, delay);
    },
    [clearSessionTimers]
  );

  setupSessionTimersRef.current = setupSessionTimers;

  const refreshSession = useCallback(
    async ({ applyLocation = false } = {}) => {
      const nowMs = Date.now();
      const sinceLast = nowMs - (lastRefreshAtRef.current || 0);
      if (sinceLast >= 0 && sinceLast < MIN_REFRESH_INTERVAL_MS) {
        clearSessionTimers();
        refreshTimeoutRef.current = setTimeout(() => {
          void refreshSessionRef.current({ applyLocation });
        }, MIN_REFRESH_INTERVAL_MS - sinceLast);
        return;
      }
      lastRefreshAtRef.current = nowMs;

      try {
        const result = await getUser();
        const decision = sessionDecision(result);

        if (decision === "refresh") {
          retryAttemptRef.current = 0;
          dropSelectionOfOtherUser(result.data);
          if (applyLocation) {
            applyUserLocation(result.data, setLocation);
          }
          storeUser(result.data);
          setupSessionTimersRef.current(result.data);
          return;
        }

        if (decision === "signOut") {
          signOutRef.current();
          return;
        }

        if (decision === "keep") {
          const kept = withRefreshedTokens(readStoredUser(), result.tokens);
          if (kept) {
            storeUser(kept);
          }
          console.warn(
            "Session refresh failed; the current login was kept",
            result.message
          );
          scheduleTransientRetry();
          return;
        }

        setUser(null);
        sessionStorage.removeItem("quser");
        clearSessionTimers();
      } catch (error) {
        console.warn("Session refresh failed; the current login was kept", error);
        if (readStoredUser()) {
          scheduleTransientRetry();
        }
      }
    },
    [
      clearSessionTimers,
      dropSelectionOfOtherUser,
      readStoredUser,
      scheduleTransientRetry,
      setLocation,
      storeUser,
    ]
  );

  refreshSessionRef.current = refreshSession;

  useEffect(() => {
    let active = true;
    (async function () {
      try {
        await refreshSessionRef.current({ applyLocation: true });
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => {
      active = false;
    };
  }, []);

  useEffect(() => onAuthRejected(() => signOutRef.current()), []);

  useEffect(() => {
    // When the provider unmounts, ensure timers are cleared
    return () => {
      clearSessionTimers();
    };
  }, [clearSessionTimers]);

  const signIn = useCallback(
    async (email, password) => {
      // Clear stale location from previous session before login to prevent
      // cross-session x-location-id header pollution (JD-293 C4 defense)
      sessionStorage.removeItem("selectedLocationId");
      sessionStorage.removeItem("selectedLocationName");
      clearSelectedOrg();
      setSelectedOrg(null);
      clearLocationsCache();

      const result = await sendSignInRequest(email, password);
      if (result.isOk) {
        applyUserLocation(result.data, setLocation);
        setUser(result.data);
        sessionStorage.setItem("quser", JSON.stringify(result.data));
        setupSessionTimers(result.data);
      } else {
        setUser(null);
        clearSessionTimers();
      }

      return result;
    },
    [clearSessionTimers, setLocation, setupSessionTimers]
  );

  const selectOrganization = useCallback(
    (org) => {
      persistSelectedOrg(org);
      const stored = readSelectedOrg();
      if (!stored || stored.org_id !== Number(org?.org_id)) {
        return;
      }

      // Locations belong to an organization, so the previous choice is void.
      persistSelectedLocation(ALL_LOCATIONS);
      if (typeof setLocation === "function") {
        setLocation(ALL_LOCATIONS);
      }
      clearLocationsCache();
      setSelectedOrg(stored);
    },
    [setLocation]
  );

  // Pages see the super admin as a member of the selected organization.
  const effectiveUser = useMemo(
    () => toEffectiveUser(user, selectedOrg),
    [user, selectedOrg]
  );
  const needsOrganization = needsOrgSelection(user, selectedOrg);

  return (
    <AuthContext.Provider
      value={{
        user: effectiveUser,
        signedInUser: user,
        selectedOrg,
        needsOrganization,
        selectOrganization,
        signIn,
        signOut,
        loading,
      }}
      {...props}
    />
  );
}

const AuthContext = createContext({});
const useAuth = () => useContext(AuthContext);

export { AuthProvider, useAuth };
